import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useMemo, useState } from "react";
import { useLang } from "../contexts/LangContext";
import { useUserData } from "../contexts/UserContext";
import { convertNumberInCurrentLang, convertWeight } from "../utils/misc";
import { getNutrientData } from "../data/nutrientLoader";
import { NUTRIENT_REFERENCE_VALUES } from "../data/nutrientReferenceValues";
import { motion } from "framer-motion";

export default function LogHistoryMonthlyStats() {
  const { personId } = useParams();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getPerson, user } = useUserData();

  const person = getPerson(personId!);
  const weight_unit = user?.appData?.weight_unit || "gm";
  
  // State for toggling between monthly total and daily average
  const [nutrientView, setNutrientView] = useState<'total' | 'average'>('total');

  // Get month and year from URL params or default to current month
  const month = parseInt(searchParams.get('month') || String(new Date().getMonth()));
  const year = parseInt(searchParams.get('year') || String(new Date().getFullYear()));

  // Calculate monthly statistics
  const monthlyStats = useMemo(() => {
    if (!person) {
      return {
        totalDaysWithLogs: 0,
        totalMeals: 0,
        totalFishWeight: 0,
        fishBySpecies: [],
        totalNutrients: {
          energy: 0,
          protein: 0,
          fat: 0,
          calcium: 0,
          iron: 0,
          zinc: 0,
          iodine: 0,
          sodium: 0,
          potassium: 0,
          magnesium: 0,
          phosphorus: 0,
          copper: 0,
          manganese: 0,
          sulphur: 0,
          selenium: 0,
          vitaminA: 0,
          vitaminD: 0,
          vitaminE: 0,
          thiamin: 0,
          riboflavin: 0,
          niacin: 0,
          vitaminB6: 0,
          folate: 0,
          vitaminB12: 0,
          omega3: 0,
        },
      };
    }

    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month + 1, 0);
    
    // Use local date strings to avoid timezone issues
    const monthStartStr = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const monthEndStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(monthEnd.getDate()).padStart(2, '0')}`;

    // Filter records for the selected month
    const monthRecords = person.records.filter(record => {
      return record.date >= monthStartStr && record.date <= monthEndStr;
    });

    // Fish consumption by species
    const fishBySpecies: Record<string, { count: number; weight: number; name_en: string; name_bn: string }> = {};
    
    // Total nutrients
    const totalNutrients: Record<string, number> = {
      energy: 0,
      protein: 0,
      fat: 0,
      calcium: 0,
      iron: 0,
      zinc: 0,
      iodine: 0,
      sodium: 0,
      potassium: 0,
      magnesium: 0,
      phosphorus: 0,
      copper: 0,
      manganese: 0,
      sulphur: 0,
      selenium: 0,
      vitaminA: 0,
      vitaminD: 0,
      vitaminE: 0,
      thiamin: 0,
      riboflavin: 0,
      niacin: 0,
      vitaminB6: 0,
      folate: 0,
      vitaminB12: 0,
      omega3: 0,
    };

    let totalDaysWithLogs = 0;
    let totalMeals = 0;
    let totalFishWeight = 0;
    let hasEntriesWithoutNutrients = false;

    monthRecords.forEach(record => {
      // Count days with fish consumption OR explicitly marked as no fish consumed
      if ((record.meals && record.meals.length > 0) || record.no_fish_consumed) {
        totalDaysWithLogs++;
      }
      
      // Only process meals if they exist
      if (record.meals && record.meals.length > 0) {
        
        record.meals.forEach(meal => {
          if (meal.fish_items && meal.fish_items.length > 0) {
            totalMeals++;
            
            meal.fish_items.forEach(item => {
              const fishId = item.fish_id;
              const weight = convertWeight(item.estimated_weight || 0, item.weight_unit || "gm", "gm");
              totalFishWeight += weight;

              // Track by species
              if (!fishBySpecies[fishId]) {
                fishBySpecies[fishId] = {
                  count: 0,
                  weight: 0,
                  name_en: item.fish_name || "Unknown",
                  name_bn: item.fish_name || "অজানা",
                };
              }
              fishBySpecies[fishId].count++;
              fishBySpecies[fishId].weight += weight;

              // Calculate nutrients
              const nutrients = getNutrientData(fishId);
              if (nutrients) {
                const weightIn100g = weight / 100;
                totalNutrients.energy += (nutrients.energy?.value || 0) * weightIn100g;
                totalNutrients.protein += (nutrients.protein?.value || 0) * weightIn100g;
                totalNutrients.fat += (nutrients.fat?.value || 0) * weightIn100g;
                totalNutrients.calcium += (nutrients.calcium?.value || 0) * weightIn100g;
                totalNutrients.iron += (nutrients.iron?.value || 0) * weightIn100g;
                totalNutrients.zinc += (nutrients.zinc?.value || 0) * weightIn100g;
                totalNutrients.iodine += (nutrients.iodine?.value || 0) * weightIn100g;
                totalNutrients.sodium += (nutrients.sodium?.value || 0) * weightIn100g;
                totalNutrients.potassium += (nutrients.potassium?.value || 0) * weightIn100g;
                totalNutrients.magnesium += (nutrients.magnesium?.value || 0) * weightIn100g;
                totalNutrients.phosphorus += (nutrients.phosphorus?.value || 0) * weightIn100g;
                totalNutrients.copper += (nutrients.copper?.value || 0) * weightIn100g;
                totalNutrients.manganese += (nutrients.manganese?.value || 0) * weightIn100g;
                totalNutrients.sulphur += (nutrients.sulphur?.value || 0) * weightIn100g;
                totalNutrients.selenium += (nutrients.selenium?.value || 0) * weightIn100g;
                totalNutrients.vitaminA += (nutrients.vitaminA?.value || 0) * weightIn100g;
                totalNutrients.vitaminD += (nutrients.vitaminD?.value || 0) * weightIn100g;
                totalNutrients.vitaminE += (nutrients.vitaminE?.value || 0) * weightIn100g;
                totalNutrients.thiamin += (nutrients.thiamin?.value || 0) * weightIn100g;
                totalNutrients.riboflavin += (nutrients.riboflavin?.value || 0) * weightIn100g;
                totalNutrients.niacin += (nutrients.niacin?.value || 0) * weightIn100g;
                totalNutrients.vitaminB6 += (nutrients.vitaminB6?.value || 0) * weightIn100g;
                totalNutrients.folate += (nutrients.folate?.value || 0) * weightIn100g;
                totalNutrients.vitaminB12 += (nutrients.vitaminB12?.value || 0) * weightIn100g;
                totalNutrients.omega3 += (nutrients.omega3?.value || 0) * weightIn100g;
              } else {
                hasEntriesWithoutNutrients = true;
              }
            });
          }
        });
      }
    });

    // Sort fish by weight
    const sortedFish = Object.entries(fishBySpecies)
      .sort((a, b) => b[1].weight - a[1].weight);

    return {
      totalDaysWithLogs,
      totalMeals,
      totalFishWeight,
      fishBySpecies: sortedFish,
      totalNutrients,
      hasEntriesWithoutNutrients,
    };
  }, [person, month, year]);

  // Calculate 12-month trend data for comparison charts
  const monthlyTrends = useMemo(() => {
    if (!person) return null;

    const currentDate = new Date(year, month);
    const currentMonthIndex = currentDate.getMonth() + currentDate.getFullYear() * 12;
    const today = new Date();
    const todayMonthIndex = today.getMonth() + today.getFullYear() * 12;
    
    // Determine which 12 months to show
    let startMonthOffset: number;
    if (todayMonthIndex - currentMonthIndex <= 5) {
      // Current month is in the last 6 months from today, show last 12 months ending with today's month
      startMonthOffset = todayMonthIndex - currentMonthIndex - 11;
    } else {
      // Show stat month as the 7th month in the chart (6 months before, stat month, 5 months after)
      startMonthOffset = -6;
    }

    // Calculate nutrients for each of the 12 months
    const monthsData: Array<{
      month: number;
      year: number;
      nutrients: Record<string, number>;
      isCurrent: boolean;
    }> = [];

    for (let i = 0; i < 12; i++) {
      const targetDate = new Date(year, month + startMonthOffset + i);
      const targetMonth = targetDate.getMonth();
      const targetYear = targetDate.getFullYear();
      
      // Use local date strings to avoid timezone issues
      const monthEnd = new Date(targetYear, targetMonth + 1, 0);
      const monthStartStr = `${targetYear}-${String(targetMonth + 1).padStart(2, '0')}-01`;
      const monthEndStr = `${targetYear}-${String(targetMonth + 1).padStart(2, '0')}-${String(monthEnd.getDate()).padStart(2, '0')}`;

      const monthRecords = person.records.filter(record => 
        record.date >= monthStartStr && record.date <= monthEndStr
      );

      const nutrients: Record<string, number> = {
        energy: 0, protein: 0, fat: 0, calcium: 0, iron: 0, zinc: 0,
        iodine: 0, sodium: 0, potassium: 0, magnesium: 0, phosphorus: 0,
        copper: 0, manganese: 0, sulphur: 0, selenium: 0, vitaminA: 0,
        vitaminD: 0, vitaminE: 0, thiamin: 0, riboflavin: 0, niacin: 0,
        vitaminB6: 0, folate: 0, vitaminB12: 0, omega3: 0,
      };

      monthRecords.forEach(record => {
        record.meals?.forEach(meal => {
          meal.fish_items?.forEach(item => {
            const fishId = item.fish_id;
            const weight = convertWeight(item.estimated_weight || 0, item.weight_unit || "gm", "gm");
            const nutrientData = getNutrientData(fishId);
            
            if (nutrientData) {
              const weightIn100g = weight / 100;
              Object.keys(nutrients).forEach(key => {
                const camelKey = key as keyof typeof nutrientData;
                nutrients[key] += (nutrientData[camelKey]?.value || 0) * weightIn100g;
              });
            }
          });
        });
      });

      monthsData.push({
        month: targetMonth,
        year: targetYear,
        nutrients,
        isCurrent: targetMonth === month && targetYear === year,
      });
    }

    return {
      months: monthsData,
      startMonth: monthsData[0].month,
      startYear: monthsData[0].year,
      endMonth: monthsData[11].month,
      endYear: monthsData[11].year,
    };
  }, [person, month, year]);

  const monthName = new Date(year, month, 1).toLocaleDateString(
    lang === "bn" ? "bn-BD" : "en-US",
    { month: "long", year: "numeric" }
  );
  
  // Calculate nutrient values based on view mode
  const displayNutrients = useMemo(() => {
    const divisor = nutrientView === 'average' && monthlyStats.totalDaysWithLogs > 0 
      ? monthlyStats.totalDaysWithLogs 
      : 1;
    
    return Object.fromEntries(
      Object.entries(monthlyStats.totalNutrients).map(([key, value]) => [
        key,
        value / divisor
      ])
    );
  }, [monthlyStats.totalNutrients, monthlyStats.totalDaysWithLogs, nutrientView]);

  // Calculate display weight based on view mode
  const displayWeight = useMemo(() => {
    const divisor = nutrientView === 'average' && monthlyStats.totalDaysWithLogs > 0 
      ? monthlyStats.totalDaysWithLogs 
      : 1;
    
    return monthlyStats.totalFishWeight / divisor;
  }, [monthlyStats.totalFishWeight, monthlyStats.totalDaysWithLogs, nutrientView]);

  const handleBack = () => {
    const from = searchParams.get('from');
    if (from === 'all_logs') {
      navigate(`/log-history/${personId}/?from=all_logs`);
    } else {
      navigate(`/log-history/${personId}/`);
    }
  };

  // Early return after all hooks have been called
  if (!person) {
    return (
      <div className="text-center mt-16 text-red-600">
        {t("logHistoryDateLog_NoPerson")}
      </div>
    );
  }

  // Nutrient display with DRI percentage
  const NutrientRow = ({ 
    label, 
    value, 
    unit, 
    driKey,
    showDRI,
    nutrientKey 
  }: { 
    label: string; 
    value: number; 
    unit: string;
    driKey?: keyof typeof NUTRIENT_REFERENCE_VALUES;
    showDRI: boolean;
    nutrientKey: string;
  }) => {
    const dri = driKey ? NUTRIENT_REFERENCE_VALUES[driKey] : null;
    const percentage = dri ? (value / dri) * 100 : null;
    const cappedPercentage = percentage ? Math.min(percentage, 100) : 0;
    
    // Choose color based on percentage
    const getProgressColor = () => {
      if (!percentage) return 'bg-gray-100';
      if (percentage >= 100) return 'bg-green-100';
      if (percentage >= 75) return 'bg-emerald-100';
      if (percentage >= 50) return 'bg-yellow-100';
      if (percentage >= 25) return 'bg-orange-100';
      return 'bg-red-100';
    };

    // Get 12-month data for this nutrient
    const monthData = monthlyTrends?.months.map(m => ({
      value: m.nutrients[nutrientKey] || 0,
      isCurrent: m.isCurrent,
    })) || [];
    
    const maxValue = Math.max(...monthData.map(m => m.value), 1);
    
    return (
      <div className="relative border-b border-gray-200 overflow-hidden">
        {/* Progress bar background - only show in daily average view */}
        {showDRI && percentage !== null && (
          <div 
            className={`absolute left-0 top-0 bottom-0 ${getProgressColor()} transition-all duration-300`}
            style={{ width: `${cappedPercentage}%` }}
          />
        )}
        
        {/* Main content row */}
        <div className="flex justify-between items-center py-2 relative z-10">
          <span className="text-gray-700">{label}</span>
          <div className="flex flex-col items-end">
            <span className="font-semibold text-gray-900">
              {convertNumberInCurrentLang(lang, parseFloat(value.toFixed(1)))} {unit}
            </span>
            {showDRI && percentage !== null && (
              <span className={`text-xs ${percentage >= 100 ? 'text-green-600' : 'text-orange-600'}`}>
                {convertNumberInCurrentLang(lang, Math.round(percentage))}% {t("monthlyStats_ofDRI")}
              </span>
            )}
          </div>
        </div>

        {/* 12-month comparison chart - only show in monthly total view */}
        {!showDRI && monthlyTrends && (
          <div className="relative px-1 pb-1">
            {/* Chart with bars */}
            <div className="flex items-end gap-0.5 h-10 relative">
              {monthData.map((data, idx) => {
                const heightPercent = maxValue > 0 ? (data.value / maxValue) * 100 : 0;
                const monthDate = monthlyTrends.months[idx];
                return (
                  <div key={idx} className="flex-1 relative h-full">
                    {/* Vertical grid line */}
                    <div className="absolute bottom-0 left-0 w-px h-full bg-gray-200" style={{ opacity: 0.3 }} />
                    
                    {/* Bar container positioned at bottom */}
                    <div className="absolute bottom-0 left-0 right-0 flex items-end">
                      <div
                        className={`w-full rounded-sm transition-all duration-200 ${
                          data.isCurrent 
                            ? 'bg-blue-500' 
                            : 'bg-gray-300 hover:bg-gray-400'
                        }`}
                        style={{ height: `${heightPercent * 0.4}px`, minHeight: data.value > 0 ? '2px' : '0' }}
                        title={`${convertNumberInCurrentLang(lang, parseFloat(data.value.toFixed(1)))} ${unit}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Month labels */}
            <div className="flex gap-0.5 mt-0.5">
              {monthlyTrends.months.map((monthDate, idx) => {
                const date = new Date(monthDate.year, monthDate.month);
                const monthLabel = date.toLocaleDateString(
                  lang === "bn" ? "bn-BD" : "en-US",
                  { month: "short" }
                );
                const yearLabel = date.toLocaleDateString(
                  lang === "bn" ? "bn-BD" : "en-US",
                  { year: "2-digit" }
                );
                return (
                  <div 
                    key={idx} 
                    className={`flex-1 text-center ${monthDate.isCurrent ? 'font-semibold text-blue-600' : 'text-gray-500'}`}
                    style={{ fontSize: '0.5rem', lineHeight: '0.65rem' }}
                  >
                    <div>{monthLabel}</div>
                    <div>{yearLabel}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <motion.div className="flex flex-col grow"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}>
      <div className="page flex flex-col grow bg-gradient-to-br from-blue-50 to-green-50 bg-opacity-50 min-h-screen">
        <div className="flex flex-col w-full px-scr py-6 space-y-6">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-3xl font-bold text-rose-700">
              {t("monthlyStats_heading")}
            </h1>
            <p className="text-xl text-blue-600 font-bold mt-2">
              {person.name || t("unknown")}
            </p>
            <p className="text-md text-gray-500">
              {monthName}
            </p>
          </div>

          {/* Overview Cards */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white/75 rounded-2xl p-4 text-center">
              <p className="text-3xl font-bold text-blue-600">
                {convertNumberInCurrentLang(lang, monthlyStats.totalDaysWithLogs)}
              </p>
              <p className="text-sm text-gray-600 mt-1">{t("monthlyStats_daysWithLogs")}</p>
            </div>
            
            <div className="bg-white/75 rounded-2xl p-4 text-center">
              <p className="text-3xl font-bold text-green-600">
                {convertNumberInCurrentLang(lang, monthlyStats.totalMeals)}
              </p>
              <p className="text-sm text-gray-600 mt-1">{t("monthlyStats_totalMeals")}</p>
            </div>
            
            <div className="bg-white/75 rounded-2xl p-4 text-center col-span-2">
              <p className="text-3xl font-bold text-purple-600">
                {convertNumberInCurrentLang(lang, parseFloat(monthlyStats.totalFishWeight.toFixed(1)))} {lang === "bn" ? "গ্রাম" : "g"}
              </p>
              <p className="text-sm text-gray-600 mt-1">{t("monthlyStats_totalFishWeight")}</p>
            </div>
          </div>

          {/* Fish by Species */}
          {monthlyStats.fishBySpecies.length > 0 && (
            <div className="bg-white/75 rounded-2xl p-5">
              <h2 className="text-xl font-bold text-gray-800 mb-4">
                {t("monthlyStats_fishBySpecies")}
              </h2>
              <div className="space-y-3">
                {monthlyStats.fishBySpecies.map(([fishId, data]) => (
                  <div key={fishId} className="flex justify-between items-center gap-2 p-3 bg-slate-200/40 rounded-lg">
                    <div>
                      <p className="font-semibold text-gray-800">
                        {lang === "bn" ? data.name_bn : data.name_en}
                      </p>
                      <p className="text-sm text-gray-500">
                        {convertNumberInCurrentLang(lang, data.count)} {t("monthlyStats_times")}
                      </p>
                    </div>
                    <p className="text-lg font-bold text-blue-600">
                      {convertNumberInCurrentLang(lang, Math.round(data.weight))} {lang === "bn" ? "গ্রাম" : "g"}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Total Nutrients */}
          <div className="bg-white/75 rounded-2xl p-5">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              {t("monthlyStats_totalNutrients")}
            </h2>
            
            {/* Toggle buttons */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setNutrientView('total')}
                className={`flex-1 py-2 px-4 rounded-full font-medium transition-all ${
                  nutrientView === 'total'
                    ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {lang === "bn" ? "মাসিক মোট" : "Monthly Total"}
              </button>
              <button
                onClick={() => setNutrientView('average')}
                className={`flex-1 py-2 px-4 rounded-full font-medium transition-all ${
                  nutrientView === 'average'
                    ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {lang === "bn" ? "দৈনিক গড়" : "Daily Average"}
              </button>
            </div>
            
            <div className="space-y-1">
              {/* Fish Weight Display */}
              <NutrientRow 
                label={t("monthlyStats_FishConsumed")} 
                value={displayWeight} 
                unit={weight_unit}
                driKey={undefined}
                showDRI={nutrientView === 'average'}
                nutrientKey="totalFishWeight"
              />
              
              <NutrientRow 
                label={t("energy")} 
                value={displayNutrients.energy} 
                unit={t("unit_kcal")}
                driKey="energy"
                showDRI={nutrientView === 'average'}
                nutrientKey="energy"
              />
              <NutrientRow 
                label={t("protein")} 
                value={displayNutrients.protein} 
                unit={lang === "bn" ? "গ্রাম" : "g"}
                driKey="protein"
                showDRI={nutrientView === 'average'}
                nutrientKey="protein"
              />
              <NutrientRow 
                label={t("fat")} 
                value={displayNutrients.fat} 
                unit={lang === "bn" ? "গ্রাম" : "g"}
                driKey="fat"
                showDRI={nutrientView === 'average'}
                nutrientKey="fat"
              />
              <NutrientRow 
                label={t("omega3")} 
                value={displayNutrients.omega3} 
                unit={t("unit_mg")}
                driKey="omega3"
                showDRI={nutrientView === 'average'}
                nutrientKey="omega3"
              />
              <NutrientRow 
                label={t("calcium")} 
                value={displayNutrients.calcium} 
                unit={t("unit_mg")}
                driKey="calcium"
                showDRI={nutrientView === 'average'}
                nutrientKey="calcium"
              />
              <NutrientRow 
                label={t("iron")} 
                value={displayNutrients.iron} 
                unit={t("unit_mg")}
                driKey="iron"
                showDRI={nutrientView === 'average'}
                nutrientKey="iron"
              />
              <NutrientRow 
                label={t("zinc")} 
                value={displayNutrients.zinc} 
                unit={t("unit_mg")}
                driKey="zinc"
                showDRI={nutrientView === 'average'}
                nutrientKey="zinc"
              />
              <NutrientRow 
                label={t("iodine")} 
                value={displayNutrients.iodine} 
                unit={t("unit_μg")}
                driKey="iodine"
                showDRI={nutrientView === 'average'}
                nutrientKey="iodine"
              />
              <NutrientRow 
                label={t("sodium")} 
                value={displayNutrients.sodium} 
                unit={t("unit_mg")}
                driKey="sodium"
                showDRI={nutrientView === 'average'}
                nutrientKey="sodium"
              />
              <NutrientRow 
                label={t("potassium")} 
                value={displayNutrients.potassium} 
                unit={t("unit_mg")}
                driKey="potassium"
                showDRI={nutrientView === 'average'}
                nutrientKey="potassium"
              />
              <NutrientRow 
                label={t("magnesium")} 
                value={displayNutrients.magnesium} 
                unit={t("unit_mg")}
                driKey="magnesium"
                showDRI={nutrientView === 'average'}
                nutrientKey="magnesium"
              />
              <NutrientRow 
                label={t("phosphorus")} 
                value={displayNutrients.phosphorus} 
                unit={t("unit_mg")}
                driKey="phosphorus"
                showDRI={nutrientView === 'average'}
                nutrientKey="phosphorus"
              />
              <NutrientRow 
                label={t("copper")} 
                value={displayNutrients.copper} 
                unit={t("unit_mg")}
                driKey="copper"
                showDRI={nutrientView === 'average'}
                nutrientKey="copper"
              />
              <NutrientRow 
                label={t("manganese")} 
                value={displayNutrients.manganese} 
                unit={t("unit_mg")}
                driKey="manganese"
                showDRI={nutrientView === 'average'}
                nutrientKey="manganese"
              />
              <NutrientRow 
                label={t("sulphur")} 
                value={displayNutrients.sulphur} 
                unit={t("unit_mg")}
                driKey="sulphur"
                showDRI={nutrientView === 'average'}
                nutrientKey="sulphur"
              />
              <NutrientRow 
                label={t("selenium")} 
                value={displayNutrients.selenium} 
                unit={t("unit_μg")}
                driKey="selenium"
                showDRI={nutrientView === 'average'}
                nutrientKey="selenium"
              />
              <NutrientRow 
                label={t("vitaminA")} 
                value={displayNutrients.vitaminA} 
                unit={t("unit_μg_rae")}
                driKey="vitaminA"
                showDRI={nutrientView === 'average'}
                nutrientKey="vitaminA"
              />
              <NutrientRow 
                label={t("vitaminD")} 
                value={displayNutrients.vitaminD} 
                unit={t("unit_μg")}
                driKey="vitaminD"
                showDRI={nutrientView === 'average'}
                nutrientKey="vitaminD"
              />
              <NutrientRow 
                label={t("vitaminE")} 
                value={displayNutrients.vitaminE} 
                unit={t("unit_mg")}
                driKey="vitaminE"
                showDRI={nutrientView === 'average'}
                nutrientKey="vitaminE"
              />
              <NutrientRow 
                label={t("thiamin")} 
                value={displayNutrients.thiamin} 
                unit={t("unit_mg")}
                driKey="thiamin"
                showDRI={nutrientView === 'average'}
                nutrientKey="thiamin"
              />
              <NutrientRow 
                label={t("riboflavin")} 
                value={displayNutrients.riboflavin} 
                unit={t("unit_mg")}
                driKey="riboflavin"
                showDRI={nutrientView === 'average'}
                nutrientKey="riboflavin"
              />
              <NutrientRow 
                label={t("niacin")} 
                value={displayNutrients.niacin} 
                unit={t("unit_mg")}
                driKey="niacin"
                showDRI={nutrientView === 'average'}
                nutrientKey="niacin"
              />
              <NutrientRow 
                label={t("vitaminB6")} 
                value={displayNutrients.vitaminB6} 
                unit={t("unit_mg")}
                driKey="vitaminB6"
                showDRI={nutrientView === 'average'}
                nutrientKey="vitaminB6"
              />
              <NutrientRow 
                label={t("folate")} 
                value={displayNutrients.folate} 
                unit={t("unit_μg")}
                driKey="folate"
                showDRI={nutrientView === 'average'}
                nutrientKey="folate"
              />
              <NutrientRow 
                label={t("vitaminB12")} 
                value={displayNutrients.vitaminB12} 
                unit={t("unit_μg")}
                driKey="vitaminB12"
                showDRI={nutrientView === 'average'}
                nutrientKey="vitaminB12"
              />            
            </div>
          </div>

          {/* Note about excluded entries without nutrient data */}
          {monthlyStats.hasEntriesWithoutNutrients && (
            <div className="bg-amber-100/60 rounded-lg p-4">
              <p className="text-sm text-amber-700">
                <span className="font-semibold">{t("monthlyStats_note")}:</span>{" "}
                {t("monthlyStats_excludedNote")}
              </p>
            </div>
          )}

          {/* Note about DRI - only show when in daily average view */}
          {nutrientView === 'average' && (
            <div className="bg-sky-200/50  rounded-lg p-4">
              <p className="text-sm text-sky-500">
                <span className="font-semibold">{t("monthlyStats_note")}:</span> {t("monthlyStats_driNote")}
              </p>
            </div>
          )}

          {/* Note about 12-month comparison - only show when in monthly total view */}
          {nutrientView === 'total' && monthlyTrends && (
            <div className="bg-sky-200/50  rounded-lg p-4">
              <p className="text-sm text-sky-500">
                <span className="font-semibold">{t("monthlyStats_note")}:</span>{" "}
                {lang === "bn" 
                  ? `চার্টগুলি ${new Date(monthlyTrends.startYear, monthlyTrends.startMonth).toLocaleDateString("bn-BD", { month: "long", year: "numeric" })} থেকে ${new Date(monthlyTrends.endYear, monthlyTrends.endMonth).toLocaleDateString("bn-BD", { month: "long", year: "numeric" })} পর্যন্ত ১২ মাসের মাসিক পুষ্টি গ্রহণের তুলনা দেখাচ্ছে। নীল বারটি বর্তমান মাস নির্দেশ করে।`
                  : `Charts show 12-month comparison of monthly nutrient intake from ${new Date(monthlyTrends.startYear, monthlyTrends.startMonth).toLocaleDateString("en-US", { month: "long", year: "numeric" })} to ${new Date(monthlyTrends.endYear, monthlyTrends.endMonth).toLocaleDateString("en-US", { month: "long", year: "numeric" })}. Blue bar indicates current month.`
                }
              </p>
            </div>
          )}
        </div>

        {/* Back Button */}
        <div className="bottom-nav-bar flex justify-center w-full sticky bottom-0 px-scr py-4 bg-white bg-opacity-75 backdrop-blur z-10 border-t">
          <button
            onClick={handleBack}
            className="button-small button-bg-grad-2 !text-lg flex flex-row gap-1 items-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="size-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
              />
            </svg>
            <span>{t("logHistoryDateLog_BackToCalendar")}</span>
          </button>
        </div>
      </div>
    </motion.div>   
  );
}
